import { EMAIL_PREFILL_QUERY_PARAM, LOGIN_LITE_ACCESS_TOKEN_QUERY_PARAM } from '@opendoor/auth-fe';

import { globalTrackEvent } from './observability';
import { OD_TEAM_BOUNDARY_ERROR, TEAM } from './ODTeamBoundary';

/**
 * TeamError is a custom error type that includes the team name and source of the error.
 * It is thrown by TeamErrorBoundary when an error occurs in a component tree that is wrapped
 * by an ODTeamBoundary.
 */
export class TeamError implements Error {
  /**
   * @param message - The error message.
   */
  public message: string;
  /**
   * @param name - The name of the error. Always OD_TEAM_BOUNDARY_ERROR.
   */
  public name: string;
  /**
   * @param team - The name of the team that owns the component tree where the error occurred.
   */
  public team: TEAM;
  /**
   * @param cause - The source of the error. This is the error that was caught by TeamErrorBoundary.
   */
  public cause: 'user-error' | 'system-error';

  constructor(error: Error, team: TEAM, cause: 'user-error' | 'system-error') {
    this.message = error.message;
    this.name = OD_TEAM_BOUNDARY_ERROR;
    this.team = team;
    this.cause = cause;
    captureErrorStackTrace(this, TeamError);
  }
}

/**
 * UserError is a custom error type that is thrown when a user error occurs.
 */
export class UserError {
  constructor(message: string) {
    return new Error(message, { cause: 'user-error' });
  }
}

/**
 * UserError is a custom error type that is thrown when a user error occurs.
 */
export class DevError {
  constructor(message: string) {
    return new Error(message, { cause: 'system-error' });
  }
}

/**
 * SystemError is a custom error type that is thrown when a system error occurs.
 * This is the default error type if the cause is not specified.
 */
export class SystemError {
  constructor(message: string) {
    return new Error(message, { cause: 'system-error' });
  }
}

/**
 * UnauthenticatedError is a custom error type that is thrown when a user is not authenticated.
 */
export class UnauthenticatedError implements Error {
  /**
   * @param message - The error message.
   */
  public message: string;
  /**
   * @param name - The name of the error. Always OD_TEAM_BOUNDARY_ERROR.
   */
  public name: string;
  /**
   * @param team - The name of the team that owns the component tree where the error occurred.
   */
  public team: TEAM;
  /**
   * @param cause - The source of the error. This is the error that was caught by TeamErrorBoundary.
   */
  public cause: 'user-error';
  /**
   * @param triggeredQuery - The triggered query that caused the error.
   */
  public triggeredQuery?: string;
  /**
   * @param emailPrefill - Whether the email prefill query param is present.
   */
  public emailPrefill: string | null;
  /**
   * @param accessToken - Whether the login lite access token query param is present.
   */
  public accessToken: string | null;
  /**
   * @param from - The source of the error. This is the error that was caught by TeamErrorBoundary.
   */
  public from: 'apollo-link' | 'data-loader' | 'app-code' | 'unknown';

  constructor(
    triggeredQuery?: string,
    from?: 'apollo-link' | 'data-loader' | 'app-code',
    team?: TEAM,
  ) {
    let emailPrefill: string | null = null;
    let accessToken: string | null = null;

    if (typeof window !== 'undefined') {
      const searchParams = new URLSearchParams(window.location.search);
      emailPrefill = searchParams.get(EMAIL_PREFILL_QUERY_PARAM);
      accessToken = searchParams.get(LOGIN_LITE_ACCESS_TOKEN_QUERY_PARAM);
    }

    this.message = triggeredQuery
      ? 'Attempted to query while unauthenticated'
      : 'User is not authenticated';
    this.name = 'UnauthenticatedError';
    this.cause = 'user-error';
    this.team = team ?? 'consumer-platform';
    this.emailPrefill = emailPrefill;
    this.accessToken = accessToken;
    this.triggeredQuery = triggeredQuery;
    this.from = from ?? 'unknown';
    captureErrorStackTrace(this, UnauthenticatedError);
  }
}

/**
 * UnauthorizedError is a custom error type that is thrown when a user is not authorized to see the experience they're attempting to access.
 */
export class UnauthorizedError implements Error {
  /**
   * @param message - The error message.
   */
  public message: string;
  /**
   * @param name - The name of the error. Always OD_TEAM_BOUNDARY_ERROR.
   */
  public name: string;
  /**
   * @param team - The name of the team that owns the component tree where the error occurred.
   */
  public team: TEAM;
  /**
   * @param cause - The source of the error. This is the error that was caught by TeamErrorBoundary.
   */
  public cause: 'user-error';
  /**
   * @param triggeredQuery - The triggered query that caused the error.
   */
  public triggeredQuery?: string;
  /**
   * @param sellDirectOfferAccessError - Whether the error is related to accessing a sell direct offer.
   */
  public sellDirectOfferAccessError: boolean;
  /**
   * @param accessToken - Whether the login lite access token query param is present.
   */
  public accessToken: string | null;
  /**
   * @param stack - The stack trace of the error.
   */
  public stack: string | undefined;

  constructor(triggeredQuery?: string, sellDirectOfferAccessError?: boolean, team?: TEAM) {
    let accessToken: string | null = null;

    if (typeof window !== 'undefined') {
      const searchParams = new URLSearchParams(window.location.search);
      accessToken = searchParams.get(LOGIN_LITE_ACCESS_TOKEN_QUERY_PARAM);
    }

    this.message = triggeredQuery
      ? 'Attempted to query unauthorized experience'
      : 'User is not authorized to access experience';
    this.name = 'UnauthorizedError';
    this.cause = 'user-error';
    this.team = team ?? 'consumer-platform';
    this.accessToken = accessToken;
    this.triggeredQuery = triggeredQuery;
    this.sellDirectOfferAccessError = sellDirectOfferAccessError ?? false;
    captureErrorStackTrace(this, UnauthorizedError);
  }
}

/**
 * captureErrorStackTrace is a helper function that captures the stack trace of an error.
 * Tracks an event if Error.captureStackTrace is not available in the browser.
 * @param targetObject - The object to capture the stack trace on.
 * @param constructorOpt - The constructor function to exclude from the stack trace.
 */
const captureErrorStackTrace = (
  targetObject: object,
  // eslint-disable-next-line @typescript-eslint/ban-types -- matching underlying method definition
  constructorOpt: Function,
): void => {
  // captureStackTrace seems to be missing for certain browsers
  if (Error.captureStackTrace) {
    // Captures the stack trace, excluding the constructor call
    Error.captureStackTrace(targetObject, constructorOpt);
  } else {
    // Intentionally not logging to sentry/datadog as an error
    globalTrackEvent('error', 'Error.captureStackTrace is not available in this browser', {
      browser: typeof window !== 'undefined' ? window.navigator.userAgent : null,
      error: targetObject,
    });
  }
};
